import { useState, useMemo } from 'react'
import * as XLSX from 'xlsx'

export interface Address {
  line1?: string
  line2?: string
  city?: string
  postcode?: string
  country?: string
}

export interface DetailRow {
  id: string
  document?: string
  waybill?: string
  mblNo?: string
  voyageNo?: string
  carrier?: string
  vessel?: string
  exportRef?: string
  shipper?: string
  shipperAddress?: Address
  consignee?: string
  consigneeAddress?: Address
  consigneeContact?: string
  consigneeEori?: string
  portLoading?: string
  portDischarge?: string
  finalDelivery?: string
  containerNo?: string
  sealNo?: string
  containerType?: string
  shippingTerms?: string
  freightType?: string
  packages?: string
  grossWeightKgs?: number
  netWeightKgs?: number
  volumeCbm?: number
  cargoDescription?: string
  summary?: string
  isAiInferred?: Record<string, boolean>
  edited?: Record<string, boolean>
}

export interface SelectOption {
  value: string
  label: string
}

interface MBLHBLTableProps {
  mode: 'single' | 'mbl_hbl' | 'bundled' | 'manifest' | 'invoices'
  // A: whole import is one transport mode (never mixed row-to-row) — drives the
  // Voyage No./Flight No. and Vessel/Aircraft column labels.
  transportMode?: 'SEA' | 'AIR' | 'ROAD' | string
  masterRow?: DetailRow
  detailRows: DetailRow[]
  // #297: per-HBL Container Type / Freight Term dropdowns render from these option lists
  // (codes, matching how sync-to-boxtrax/create-autojobs resolve them by text) instead of
  // the generic double-click text input used for every other column.
  containerTypeOptions?: SelectOption[]
  shippingTermOptions?: SelectOption[]
  onDataChange?: (rows: DetailRow[], master?: DetailRow) => void
  onContinue?: () => void
}

interface ValidationIssue {
  type: 'error' | 'warning' | 'info'
  row?: string
  column?: string
  message: string
}

// A: for AIR jobs, BoxTop/the DB have distinct fields for these two columns (Flight1 vs
// Conveyance/VoyageNumber; job_air_details.flight_no vs job_sea_details.voyage_no/vessel_name)
// so the labels swap dynamically per transport mode rather than using one dual label.
const BASE_COLUMNS = [
  { key: 'document', label: 'Document', width: 180 },
  { key: 'waybill', label: 'Waybill / BL No.', width: 150 },
  { key: 'mblNo', label: 'MBL No.', width: 140 },
  { key: 'voyageNo', label: 'Voyage No.', width: 120 },
  { key: 'carrier', label: 'Carrier', width: 180 },
  { key: 'vessel', label: 'Vessel', width: 180 },
  { key: 'exportRef', label: 'Export Ref', width: 120 },
  { key: 'shipper', label: 'Shipper', width: 180 },
  { key: 'shipperAddress', label: 'Shipper Address', width: 200 },
  { key: 'consignee', label: 'Consignee', width: 180 },
  { key: 'consigneeAddress', label: 'Consignee Address', width: 200 },
  { key: 'consigneeContact', label: 'Consignee Contact', width: 160 },
  { key: 'portLoading', label: 'Port of Loading', width: 140 },
  { key: 'portDischarge', label: 'Port of Discharge', width: 140 },
  { key: 'finalDelivery', label: 'Final Delivery', width: 140 },
  { key: 'containerNo', label: 'Container No.', width: 140 },
  { key: 'sealNo', label: 'Seal No.', width: 120 },
  { key: 'containerType', label: 'Container Type', width: 120 },
  { key: 'shippingTerms', label: 'Freight Term', width: 120 },
  { key: 'freightType', label: 'Freight Type', width: 110 },
  { key: 'packages', label: 'Packages', width: 120 },
  { key: 'grossWeightKgs', label: 'Gross Weight (KGS)', width: 140 },
  { key: 'netWeightKgs', label: 'Net Weight (KGS)', width: 140 },
  { key: 'volumeCbm', label: 'Volume (CBM)', width: 140 },
  { key: 'cargoDescription', label: 'Cargo Description', width: 200 },
  { key: 'summary', label: 'Summary', width: 200 }
]

const FREIGHT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'prepaid', label: 'Prepaid' },
  { value: 'collect', label: 'Collect' },
]

// Z15(B): staff often forget to set these, so Freight Term/Type are required on every
// row (master + all HBLs) before continuing -- applies to every mode, including
// "MBL + HBLs from Invoices" (CI Upload), since they all share this table.
const REQUIRED_COLUMNS = ['shippingTerms', 'freightType']

function safeParsePackages(pkg: any): number {
  if (!pkg) return 0
  const str = typeof pkg === 'string' ? pkg : String(pkg)
  const match = str.match(/(\d+)/)
  return match ? parseInt(match[1]) : 0
}

const runValidations = (masterRow: DetailRow | undefined, detailRows: DetailRow[]): ValidationIssue[] => {
  const issues: ValidationIssue[] = []

  // Check if MBL is present (if needed)
  if (masterRow === undefined && detailRows.length > 0) {
    issues.push({
      type: 'error',
      message: 'MBL is missing but HBLs are present'
    })
  }

  // Check for duplicate HBL numbers
  const waybillNumbers = new Map<string, number>()
  detailRows.forEach((row) => {
    if (row.waybill) {
      const count = (waybillNumbers.get(row.waybill) || 0) + 1
      waybillNumbers.set(row.waybill, count)
    }
  })

  waybillNumbers.forEach((count, waybill) => {
    if (count > 1) {
      issues.push({
        type: 'warning',
        message: `HBL number "${waybill}" appears ${count} times`
      })
    }
  })

  // Check packages sum
  if (masterRow && detailRows.length > 0) {
    const masterPackages = safeParsePackages(masterRow.packages)
    const detailSum = detailRows.reduce((sum, row) => sum + safeParsePackages(row.packages), 0)

    if (detailSum > masterPackages * 1.05) {
      issues.push({
        type: 'warning',
        message: `Total packages (${detailSum}) exceeds MBL packages (${masterPackages}) by >5%`
      })
    }
  }

  // Check EORI/Country mismatch
  detailRows.forEach((row) => {
    if (row.consigneeEori && row.consigneeAddress?.country) {
      const eoriStr = typeof row.consigneeEori === 'string' ? row.consigneeEori : String(row.consigneeEori)
      const countryStr = typeof row.consigneeAddress.country === 'string' ? row.consigneeAddress.country : String(row.consigneeAddress.country)
      const eoriCountry = eoriStr.slice(0, 2).toUpperCase()
      const addressCountry = countryStr.toUpperCase()
      if (eoriCountry !== addressCountry && eoriCountry !== 'XX') {
        issues.push({
          type: 'warning',
          row: row.id,
          message: `EORI country (${eoriCountry}) doesn't match address country (${addressCountry})`
        })
      }
    }
  })

  return issues
}

export default function MBLHBLTable({
  mode,
  transportMode,
  masterRow,
  detailRows,
  containerTypeOptions = [],
  shippingTermOptions = [],
  onDataChange,
  onContinue
}: MBLHBLTableProps) {
  const isAir = (transportMode || 'SEA').toUpperCase() === 'AIR'
  const columns = useMemo(() => BASE_COLUMNS.map(col => {
    if (col.key === 'voyageNo') return { ...col, label: isAir ? 'Flight No.' : 'Voyage No.' }
    if (col.key === 'vessel') return { ...col, label: isAir ? 'Aircraft' : 'Vessel' }
    return col
  }), [isAir])
  const [editingCell, setEditingCell] = useState<{ rowId: string; column: string } | null>(null)
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set())
  const [localRows, setLocalRows] = useState<DetailRow[]>(detailRows)
  const [localMasterRow, setLocalMasterRow] = useState<DetailRow | undefined>(masterRow)
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateCarrier, setTemplateCarrier] = useState(masterRow?.carrier || '')
  const [templateTransport, setTemplateTransport] = useState('SEA')
  const [templateDirection, setTemplateDirection] = useState('IMP')
  const [detectionKeywords, setDetectionKeywords] = useState<string[]>([])
  const [keywordEdit, setKeywordEdit] = useState('')
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [templateSaveMessage, setTemplateSaveMessage] = useState('')

  const validations = useMemo(() => runValidations(localMasterRow, localRows), [localMasterRow, localRows])

  // Z15(B): count of rows (master + HBLs) missing a required Freight Term/Type value --
  // blocks Continue until every row has both set.
  const missingRequiredCount = useMemo(() => {
    const rowsToCheck = localMasterRow ? [localMasterRow, ...localRows] : localRows
    return rowsToCheck.reduce((count, r) => count + REQUIRED_COLUMNS.filter(key => !r[key as keyof DetailRow]).length, 0)
  }, [localMasterRow, localRows])

  const calculateSums = () => {
    let totalPackages = 0
    let totalGW = 0
    let totalNW = 0
    let totalCBM = 0

    // Only sum HBL details (detail rows), not the master row
    // This is used to compare against the master (MBL) data
    localRows.forEach(row => {
      totalPackages += safeParsePackages(row.packages)
      totalGW += row.grossWeightKgs || 0
      totalNW += row.netWeightKgs || 0
      totalCBM += row.volumeCbm || 0
    })

    return { totalPackages, totalGW, totalNW, totalCBM }
  }

  const sums = calculateSums()

  const handleCellEdit = (rowId: string, column: string, value: any) => {
    if (rowId === 'master') {
      const updated = {
        ...localMasterRow!,
        [column]: value,
        edited: { ...localMasterRow?.edited, [column]: true }
      }
      setLocalMasterRow(updated)
      onDataChange?.(localRows, updated)
    } else {
      const updatedRows = localRows.map(row =>
        row.id === rowId
          ? {
              ...row,
              [column]: value,
              edited: { ...row.edited, [column]: true }
            }
          : row
      )
      setLocalRows(updatedRows)
      onDataChange?.(updatedRows, localMasterRow)
    }
    setEditingCell(null)
  }

  // #12/#14: push-down — copy a column's current master (MBL) row value onto every detail
  // (HBL) row, triggered by a small ⬇ icon in the master row's own cell. Replaces the old
  // shared "apply to all rows" dropdown, and generalizes it to any column (not just
  // Container Type / Freight Term).
  const pushMasterValueToAllRows = (column: string) => {
    if (!localMasterRow) return
    const value = (localMasterRow as any)[column]
    setLocalRows(prev => {
      const updated = prev.map(row => ({
        ...row,
        [column]: value,
        edited: { ...row.edited, [column]: true },
      }))
      onDataChange?.(updated, localMasterRow)
      return updated
    })
  }

  // #14: fill-down — copy the row directly above's value for this column into the current
  // row, via a small icon per cell or Ctrl+D while editing. First detail row has no "row
  // above" within detailRows (the master row uses push-down instead), so it's a no-op there.
  const fillDownFromAbove = (rowIndex: number, column: string) => {
    if (rowIndex <= 0) return
    const value = (localRows[rowIndex - 1] as any)[column]
    handleCellEdit(localRows[rowIndex].id, column, value)
  }

  const handleExport = () => {
    const wsData = []

    // Headers
    wsData.push(columns.map(col => col.label))

    // Master row
    if (localMasterRow) {
      wsData.push(columns.map(col => getMappedValue(localMasterRow, col.key)))
    }

    // Detail rows
    localRows.forEach(row => {
      wsData.push(columns.map(col => getMappedValue(row, col.key)))
    })

    const ws = XLSX.utils.aoa_to_sheet(wsData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Batch Summary')
    XLSX.writeFile(wb, `Batch_Summary_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  // #297/#13: containerType/shippingTerms/freightType cells render a <select> instead of the
  // generic text input — codes only, since sync-to-boxtrax/create-autojobs resolve them by
  // exact code text (freightType matches job_sea_details.freight_type's own CHECK constraint).
  const dropdownOptionsFor = (columnKey: string): SelectOption[] | null => {
    if (columnKey === 'containerType') return containerTypeOptions
    if (columnKey === 'shippingTerms') return shippingTermOptions
    if (columnKey === 'freightType') return FREIGHT_TYPE_OPTIONS
    return null
  }

  const getMappedValue = (row: DetailRow, key: string): any => {
    const value = (row as any)[key]

    if (key === 'shipperAddress' || key === 'consigneeAddress') {
      if (!value) return ''
      const addr = value as Address
      return [addr.line1, addr.line2, addr.city, addr.postcode, addr.country]
        .filter(Boolean)
        .join(', ')
    }

    if (value === undefined || value === null) return ''

    // Ensure string fields are actually strings
    if (typeof value === 'number') return String(value)

    return value
  }

  const errorCount = validations.filter(v => v.type === 'error').length
  const warningCount = validations.filter(v => v.type === 'warning').length
  const infoCount = validations.filter(v => v.type === 'info').length

  return (
    <div className="space-y-4">
      {/* Validation Summary */}
      {(errorCount > 0 || warningCount > 0 || infoCount > 0) && (
        <div className="flex gap-2 flex-wrap">
          {errorCount > 0 && (
            <div className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-sm font-medium">
              🔴 {errorCount} error{errorCount !== 1 ? 's' : ''}
            </div>
          )}
          {warningCount > 0 && (
            <div className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded text-sm font-medium">
              🟠 {warningCount} warning{warningCount !== 1 ? 's' : ''}
            </div>
          )}
          {infoCount > 0 && (
            <div className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded text-sm font-medium">
              🟡 {infoCount} info
            </div>
          )}
        </div>
      )}

      {/* Action Bar - Sticky */}
      <div className="sticky top-0 z-40 flex gap-2 flex-wrap bg-white dark:bg-gray-900 p-4 border-b border-gray-300 dark:border-gray-600">
        <button
          onClick={() => {
            const newRow: DetailRow = {
              id: crypto.randomUUID(),
              document: '',
              waybill: '',
              mblNo: '',
              voyageNo: '',
              carrier: '',
              vessel: '',
              exportRef: '',
              shipper: '',
              shipperAddress: undefined,
              consignee: '',
              consigneeAddress: undefined,
              consigneeContact: '',
              consigneeEori: '',
              portLoading: '',
              portDischarge: '',
              finalDelivery: '',
              containerNo: '',
              sealNo: '',
              containerType: '',
              shippingTerms: '',
              freightType: '',
              packages: '',
              grossWeightKgs: undefined,
              netWeightKgs: undefined,
              volumeCbm: undefined,
              cargoDescription: '',
              summary: '',
              isAiInferred: {},
              edited: {}
            }
            const updated = [...localRows, newRow]
            setLocalRows(updated)
            onDataChange?.(updated)
          }}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          + Add HBL
        </button>
        <button
          onClick={() => {
            const updated = localRows.filter(r => !selectedRows.has(r.id))
            setLocalRows(updated)
            onDataChange?.(updated)
            setSelectedRows(new Set())
          }}
          disabled={selectedRows.size === 0}
          className="px-3 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
        >
          - Delete Selected
        </button>
        <button
          onClick={handleExport}
          className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
        >
          📥 Export Excel
        </button>
        <button
          onClick={onContinue}
          disabled={missingRequiredCount > 0}
          title={missingRequiredCount > 0 ? 'Fill in the required Freight Term/Type cells (highlighted in red) before continuing' : undefined}
          className="px-3 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ✓ Continue to Address Verification
        </button>
      </div>

      {missingRequiredCount > 0 && (
        <div className="px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-sm font-medium">
          🔴 {missingRequiredCount} required Freight Term/Type value{missingRequiredCount !== 1 ? 's' : ''} missing (highlighted in red below) — fill these in before continuing
        </div>
      )}

      {/* Edit Hint */}
      <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
        💡 Double-click any cell to edit · hover a cell for ⬇ to fill down from the row above (or Ctrl+D while editing) · master row's ⬇ pushes its value to all HBL rows
      </div>

      {/* Table */}
      <div className="overflow-auto border border-gray-300 dark:border-gray-600 rounded-lg max-h-[600px]">
        <table className="w-full text-sm border-collapse table-fixed">
          <thead className="sticky top-0 z-30 bg-gray-100 dark:bg-gray-700">
            <tr className="h-10 bg-gray-100 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
              <th className="w-8 px-2 py-0 sticky left-0 z-30 bg-gray-100 dark:bg-gray-700 align-middle overflow-hidden">
                <input type="checkbox" />
              </th>
              {columns.map((col, idx) => {
                const isSticky = idx < 2
                const leftPos = idx === 0 ? 32 : 180
                return (
                  <th
                    key={col.key}
                    className={`px-3 py-0 text-left font-semibold text-gray-900 dark:text-white whitespace-nowrap border-r border-gray-300 dark:border-gray-600 align-middle overflow-hidden ${
                      isSticky ? `sticky z-30 bg-gray-100 dark:bg-gray-700` : ''
                    }`}
                    style={{
                      width: `${col.width}px`,
                      minWidth: `${col.width}px`,
                      ...(isSticky && { left: `${leftPos}px` })
                    }}
                  >
                    {col.label}
                    {REQUIRED_COLUMNS.includes(col.key) && <span className="text-red-500 ml-0.5">*</span>}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {/* Master Row - Sticky Top */}
            {localMasterRow && (
              <tr className="sticky top-10 z-20 h-[60px] bg-blue-100 dark:bg-blue-900/30 border-b-2 border-blue-300 dark:border-blue-700">
                <td className="w-8 px-2 py-0 sticky left-0 z-20 bg-blue-100 dark:bg-blue-900/30 max-h-[60px] overflow-hidden align-middle">
                  <input type="checkbox" disabled />
                </td>
                {columns.map((col, idx) => {
                  const isSticky = idx < 2
                  const leftPos = idx === 0 ? 32 : 180
                  const isMissingRequired = REQUIRED_COLUMNS.includes(col.key) && !getMappedValue(localMasterRow, col.key)
                  const masterBg = isMissingRequired ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-100 dark:bg-blue-900/30'
                  return (
                    <td
                      key={col.key}
                      title={isMissingRequired ? `${col.label} is required` : getMappedValue(localMasterRow, col.key)}
                      className={`px-3 py-0 border-r ${isMissingRequired ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} text-gray-900 dark:text-white font-medium max-h-[60px] overflow-hidden align-middle cursor-text group relative ${masterBg} ${
                        isSticky ? `sticky z-20 ${masterBg}` : ''
                      }`}
                      style={{
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        ...(isSticky && { left: `${leftPos}px` })
                      }}
                      onDoubleClick={() => setEditingCell({ rowId: 'master', column: col.key })}
                    >
                      <span className="opacity-0 group-hover:opacity-50 absolute top-0 right-0 text-xs">✏️</span>
                      {localRows.length > 0 && (
                        <button
                          type="button"
                          title="Push this value down to all HBL rows"
                          onClick={e => { e.stopPropagation(); pushMasterValueToAllRows(col.key) }}
                          className="opacity-0 group-hover:opacity-70 hover:!opacity-100 absolute top-0 right-4 text-xs leading-none"
                        >
                          ⬇
                        </button>
                      )}
                      {editingCell?.rowId === 'master' && editingCell?.column === col.key ? (
                        dropdownOptionsFor(col.key) ? (
                          <select
                            autoFocus
                            defaultValue={getMappedValue(localMasterRow, col.key)}
                            onChange={e => handleCellEdit('master', col.key, e.target.value)}
                            onBlur={() => setEditingCell(null)}
                            className="w-full px-1 py-1 border border-blue-500 rounded dark:bg-gray-600 dark:text-white"
                          >
                            <option value="">—</option>
                            {dropdownOptionsFor(col.key)!.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        ) : (
                          <input
                            type="text"
                            autoFocus
                            defaultValue={getMappedValue(localMasterRow, col.key)}
                            onBlur={e => handleCellEdit('master', col.key, e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleCellEdit('master', col.key, e.currentTarget.value)
                              if (e.key === 'Escape') setEditingCell(null)
                            }}
                            className="w-full px-1 py-1 border border-blue-500 rounded dark:bg-gray-600 dark:text-white"
                          />
                        )
                      ) : (
                        <div className={['shipperAddress', 'consigneeAddress', 'cargoDescription', 'summary'].includes(col.key) ? 'line-clamp-2 text-xs max-h-[50px] overflow-hidden' : 'whitespace-nowrap'}>
                          {getMappedValue(localMasterRow, col.key)}
                        </div>
                      )}
                    </td>
                  )
                })}
              </tr>
            )}

            {/* Detail Rows */}
            {localRows.map((row, rowIndex) => (
              <tr key={row.id} className="h-[60px] border-b border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="w-8 px-2 py-0 sticky left-0 z-10 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 max-h-[60px] overflow-hidden align-middle">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.id)}
                    onChange={e => {
                      const newSelected = new Set(selectedRows)
                      if (e.target.checked) {
                        newSelected.add(row.id)
                      } else {
                        newSelected.delete(row.id)
                      }
                      setSelectedRows(newSelected)
                    }}
                  />
                </td>
                {columns.map((col, idx) => {
                  const isSticky = idx < 2
                  const leftPos = idx === 0 ? 32 : 180
                  const isDiffHighlight = localMasterRow && ['mblNo', 'voyageNo', 'carrier'].includes(col.key) &&
                    getMappedValue(row, col.key) &&
                    getMappedValue(row, col.key) !== getMappedValue(localMasterRow, col.key)
                  const isMissingRequired = REQUIRED_COLUMNS.includes(col.key) && !getMappedValue(row, col.key)
                  const detailBg = (isDiffHighlight || isMissingRequired) ? 'bg-red-50 dark:bg-red-900/20' : 'bg-white dark:bg-gray-800'

                  return (
                    <td
                      key={`${row.id}-${col.key}`}
                      className={`px-3 py-0 border-r ${isMissingRequired ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} text-gray-900 dark:text-white cursor-text group relative max-h-[60px] overflow-hidden align-middle ${detailBg} ${isSticky ? `sticky z-10 ${detailBg}` : ''}`}
                      style={{
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        ...(isSticky && { left: `${leftPos}px` })
                      }}
                      title={isMissingRequired ? `${col.label} is required` : isDiffHighlight ? `Master: ${getMappedValue(localMasterRow, col.key)}` : getMappedValue(row, col.key)}
                      onDoubleClick={() => setEditingCell({ rowId: row.id, column: col.key })}
                    >
                      <span className="opacity-0 group-hover:opacity-50 absolute top-0 right-0 text-xs">✏️</span>
                      {rowIndex > 0 && (
                        <button
                          type="button"
                          title="Fill down from row above (Ctrl+D)"
                          onClick={e => { e.stopPropagation(); fillDownFromAbove(rowIndex, col.key) }}
                          className="opacity-0 group-hover:opacity-70 hover:!opacity-100 absolute top-0 right-4 text-xs leading-none"
                        >
                          ⬇
                        </button>
                      )}
                      {editingCell?.rowId === row.id && editingCell?.column === col.key ? (
                        dropdownOptionsFor(col.key) ? (
                          <select
                            autoFocus
                            defaultValue={getMappedValue(row, col.key)}
                            onChange={e => handleCellEdit(row.id, col.key, e.target.value)}
                            onBlur={() => setEditingCell(null)}
                            onKeyDown={e => {
                              if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
                                e.preventDefault()
                                fillDownFromAbove(rowIndex, col.key)
                              }
                            }}
                            className="w-full px-1 py-1 border border-blue-500 rounded dark:bg-gray-600 dark:text-white"
                          >
                            <option value="">—</option>
                            {dropdownOptionsFor(col.key)!.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        ) : (
                          <input
                            type="text"
                            autoFocus
                            defaultValue={getMappedValue(row, col.key)}
                            onBlur={e => handleCellEdit(row.id, col.key, e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleCellEdit(row.id, col.key, e.currentTarget.value)
                              if (e.key === 'Escape') setEditingCell(null)
                              if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
                                e.preventDefault()
                                fillDownFromAbove(rowIndex, col.key)
                              }
                            }}
                            className="w-full px-1 py-1 border border-blue-500 rounded dark:bg-gray-600 dark:text-white"
                          />
                        )
                      ) : (
                        <span className={`${['shipperAddress', 'consigneeAddress', 'cargoDescription', 'summary'].includes(col.key) ? 'line-clamp-2 text-xs max-h-[50px] overflow-hidden block' : 'whitespace-nowrap'} ${row.edited?.[col.key] ? 'text-green-600 dark:text-green-400' : ''}`}>
                          {row.isAiInferred?.[col.key] ? (
                            <span className="italic text-gray-400">{getMappedValue(row, col.key)} 🤖</span>
                          ) : (
                            getMappedValue(row, col.key)
                          )}
                        </span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}

            {/* Summary Row - Shows HBL totals vs MBL values with reconciliation status.
                #298: only meaningful when there's at least one detail row to reconcile against
                the master — a single-document job (no HBL detail rows) has nothing to sum, and
                showing "∑ 0 HBL" with a full-negative delta against the master's own totals was
                being misread as a real mismatch. */}
            {localRows.length > 0 && (
              <tr className="h-[60px] bg-yellow-50 dark:bg-yellow-900/20 border-t-2 border-gray-300 dark:border-gray-600">
                <td className="w-8 px-2 py-0 sticky left-0 z-10 bg-yellow-50 dark:bg-yellow-900/20 font-semibold text-gray-900 dark:text-white max-h-[60px] overflow-hidden align-middle">
                  ∑ {localRows.length} HBL{localRows.length !== 1 ? 's' : ''}
                </td>
                {columns.map((col, idx) => {
                  const isSticky = idx < 2
                  const leftPos = idx === 0 ? 32 : 180
                  let cellContent: any = null
                  let isMatched = false

                  const checkMatch = (hblSum: number, mblValue?: number | string) => {
                    if (!mblValue && mblValue !== 0) return { matched: false, diff: 0 }
                    const mblNum = typeof mblValue === 'string' ? parseFloat(mblValue) : mblValue
                    if (isNaN(mblNum)) return { matched: false, diff: 0 }
                    const diff = Math.abs(hblSum - mblNum)
                    const tolerance = mblNum * 0.005 // 0.5% tolerance
                    return { matched: diff <= tolerance, diff: hblSum - mblNum }
                  }

                  if (col.key === 'packages') {
                    const masterPkg = localMasterRow ? safeParsePackages(localMasterRow.packages) : null
                    const match = checkMatch(sums.totalPackages, masterPkg ?? undefined)
                    isMatched = match.matched && masterPkg !== null
                    if (masterPkg !== null) {
                      const status = isMatched ? '✓' : `✗ (${match.diff > 0 ? '+' : ''}${match.diff.toFixed(0)})`
                      const statusColor = isMatched ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      cellContent = (
                        <span>
                          {sums.totalPackages} / {masterPkg}{' '}
                          <span className={statusColor}>{status}</span>
                        </span>
                      )
                    } else {
                      cellContent = String(sums.totalPackages)
                    }
                  } else if (col.key === 'grossWeightKgs') {
                    const masterGW = localMasterRow?.grossWeightKgs
                    const match = checkMatch(sums.totalGW, masterGW)
                    isMatched = match.matched && masterGW !== undefined
                    if (masterGW !== undefined) {
                      const status = isMatched ? '✓' : `✗ (${match.diff > 0 ? '+' : ''}${match.diff.toFixed(1)})`
                      const statusColor = isMatched ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      cellContent = (
                        <span>
                          {sums.totalGW.toFixed(1)} / {masterGW}{' '}
                          <span className={statusColor}>{status}</span>
                        </span>
                      )
                    } else {
                      cellContent = sums.totalGW.toFixed(1)
                    }
                  } else if (col.key === 'netWeightKgs') {
                    const masterNW = localMasterRow?.netWeightKgs
                    const match = checkMatch(sums.totalNW, masterNW)
                    isMatched = match.matched && masterNW !== undefined
                    if (masterNW !== undefined) {
                      const status = isMatched ? '✓' : `✗ (${match.diff > 0 ? '+' : ''}${match.diff.toFixed(1)})`
                      const statusColor = isMatched ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      cellContent = (
                        <span>
                          {sums.totalNW.toFixed(1)} / {masterNW}{' '}
                          <span className={statusColor}>{status}</span>
                        </span>
                      )
                    } else {
                      cellContent = sums.totalNW.toFixed(1)
                    }
                  } else if (col.key === 'volumeCbm') {
                    const masterCBM = localMasterRow?.volumeCbm
                    const match = checkMatch(sums.totalCBM, masterCBM)
                    isMatched = match.matched && masterCBM !== undefined
                    if (masterCBM !== undefined) {
                      const status = isMatched ? '✓' : `✗ (${match.diff > 0 ? '+' : ''}${match.diff.toFixed(2)})`
                      const statusColor = isMatched ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      cellContent = (
                        <span>
                          {sums.totalCBM.toFixed(2)} / {masterCBM}{' '}
                          <span className={statusColor}>{status}</span>
                        </span>
                      )
                    } else {
                      cellContent = sums.totalCBM.toFixed(2)
                    }
                  }

                  return (
                    <td
                      key={`sum-${col.key}`}
                      className={`px-3 py-0 border-r border-gray-300 dark:border-gray-600 font-semibold text-gray-900 dark:text-white max-h-[60px] overflow-hidden align-middle text-xs ${
                        isSticky ? `sticky z-10 bg-yellow-50 dark:bg-yellow-900/20` : ''
                      }`}
                      style={{
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        ...(isSticky && { left: `${leftPos}px` })
                      }}
                    >
                      {cellContent || '—'}
                    </td>
                  )
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Save Template Dialog */}
      {showSaveTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Save as Template {mode === 'manifest' && '(MBL + Manifest workflow)'} {mode === 'bundled' && '(MBL + HBLs workflow)'} {mode === 'invoices' && '(MBL + Invoices workflow)'}
            </h2>

            {templateSaveMessage && (
              <div className={`text-sm p-3 rounded ${
                templateSaveMessage.startsWith('✓')
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {templateSaveMessage}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., JL088 ICN-LHR Manifest"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={isSavingTemplate}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Carrier/Agent Name (MBL/Master) *
                </label>
                <input
                  type="text"
                  value={templateCarrier}
                  onChange={(e) => setTemplateCarrier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={isSavingTemplate}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Transport Mode *
                  </label>
                  <select
                    value={templateTransport}
                    onChange={(e) => setTemplateTransport(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={isSavingTemplate}
                  >
                    <option value="SEA">SEA</option>
                    <option value="AIR">AIR</option>
                    <option value="ROAD">ROAD</option>
                    <option value="RAIL">RAIL</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Direction
                  </label>
                  <select
                    value={templateDirection}
                    onChange={(e) => setTemplateDirection(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={isSavingTemplate}
                  >
                    <option value="IMP">IMP</option>
                    <option value="EXP">EXP</option>
                  </select>
                </div>
              </div>

              {/* Template Coverage Info */}
              {(localMasterRow || localRows.length > 0) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Template covers:</p>
                  <div className="space-y-1 text-xs text-blue-800 dark:text-blue-400">
                    {localMasterRow && (
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>{localMasterRow.document || 'MBL'}/MAWB extraction pattern</span>
                      </div>
                    )}
                    {localRows.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span>✓</span>
                        <span>{localRows[0]?.document || 'HBL/HAWB/Invoice'} rows extraction pattern ({localRows.length} row{localRows.length !== 1 ? 's' : ''})</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Detection Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Detection Keywords (auto-detected)
                  <button
                    onClick={() => setKeywordEdit('')}
                    className="float-right text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    ✏️ Edit
                  </button>
                </label>
                {keywordEdit === '' ? (
                  <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-700 rounded min-h-[32px]">
                    {detectionKeywords.length > 0 ? (
                      detectionKeywords.map((keyword, idx) => (
                        <span key={idx} className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">
                          {keyword}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Click Edit to add keywords</span>
                    )}
                  </div>
                ) : (
                  <textarea
                    value={keywordEdit}
                    onChange={(e) => setKeywordEdit(e.target.value)}
                    onBlur={() => {
                      const keywords = keywordEdit
                        .split(',')
                        .map(k => k.trim())
                        .filter(k => k.length > 0)
                      setDetectionKeywords(keywords)
                    }}
                    placeholder="Enter keywords separated by commas"
                    className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    rows={2}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., OOCL ABU DHABI SEA HBL"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={isSavingTemplate}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Carrier/Agent Name *
                </label>
                <input
                  type="text"
                  value={templateCarrier}
                  onChange={(e) => setTemplateCarrier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Transport Mode *
                  </label>
                  <select
                    value={templateTransport}
                    onChange={(e) => setTemplateTransport(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="SEA">SEA</option>
                    <option value="AIR">AIR</option>
                    <option value="ROAD">ROAD</option>
                    <option value="RAIL">RAIL</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Direction
                  </label>
                  <select
                    value={templateDirection}
                    onChange={(e) => setTemplateDirection(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="IMP">IMP</option>
                    <option value="EXP">EXP</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                onClick={() => {
                  setShowSaveTemplate(false)
                  setTemplateName('')
                  setTemplateCarrier(masterRow?.carrier || '')
                  setTemplateTransport('SEA')
                  setTemplateDirection('IMP')
                  setDetectionKeywords([])
                  setKeywordEdit('')
                  setTemplateSaveMessage('')
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (templateName && templateCarrier) {
                    setIsSavingTemplate(true)
                    setTemplateSaveMessage('')
                    try {
                      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
                      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

                      if (!supabaseUrl || !anonKey) {
                        throw new Error('Missing Supabase configuration')
                      }

                      // Save workflow template (master + all details together)
                      const response = await fetch(`${supabaseUrl}/functions/v1/save-template`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${anonKey}`,
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          template_name: templateName,
                          carrier_agent_name: templateCarrier,
                          transport_mode: templateTransport,
                          direction: templateDirection,
                          template_type: `workflow_${mode}`,
                          mode: mode,
                          detection_keywords: detectionKeywords.length > 0 ? detectionKeywords : null,
                          parsed_data: {
                            master: localMasterRow,
                            details: localRows
                          }
                        })
                      })

                      const data = await response.json()

                      if (!response.ok) {
                        throw new Error(data.error || 'Failed to save template')
                      }

                      setTemplateSaveMessage(`✓ Template '${templateName}' saved successfully`)
                      setTimeout(() => {
                        setShowSaveTemplate(false)
                        setTemplateName('')
                        setTemplateCarrier(masterRow?.carrier || '')
                        setTemplateTransport('SEA')
                        setTemplateDirection('IMP')
                        setDetectionKeywords([])
                        setKeywordEdit('')
                        setTemplateSaveMessage('')
                      }, 1500)
                    } catch (err) {
                      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
                      setTemplateSaveMessage(`✗ Error: ${errorMsg}`)
                      console.error('[MBLHBLTable] Failed to save template:', err)
                    } finally {
                      setIsSavingTemplate(false)
                    }
                  }
                }}
                disabled={!templateName || !templateCarrier || isSavingTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {isSavingTemplate ? 'Saving...' : 'Save Workflow Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
